/**
 * Каталог пряников — вкладки, параметры запроса, форматирование.
 */

import { formatGramsOne, formatSizeMm } from '../gingerbread-studio/format'
import { formatNumber } from '../products-v2/lib'
import type { GingerbreadFilterParams } from './api'
import type {
  GingerbreadBlock,
  GingerbreadColumnProfile,
  GingerbreadFilters,
  GingerbreadGrouping,
  GingerbreadGroup,
  GingerbreadRow,
  GingerbreadTabKey,
} from './types'

// ── Вкладки ─────────────────────────────────────────────────────────

export interface GingerbreadTabDef {
  key: GingerbreadTabKey
  label: string
  /** Параметры, которые вкладка накладывает поверх фильтров страницы. */
  params: GingerbreadFilterParams
}

/**
 * Вкладка — набор серверных фильтров, а не отдельный экран. «Пряники» и
 * «Наборы» делят каталог по виду (PP0 / PP1), остальные смотрят на оба вида.
 * Правила совпадают с вкладками каталога товаров: «Нет на ...» — это
 * `marketplace=no_*`, «Есть карточка» — есть карточка МП в любом статусе
 * (`hasCard`, как в `facets.tabs`), «Архив» — `archived`.
 */
export const GINGERBREAD_TABS: GingerbreadTabDef[] = [
  { key: 'kit', label: 'Наборы для продажи', params: { kind: 'kit' } },
  { key: 'single', label: 'Пряники — компоненты', params: { kind: 'single' } },
  { key: 'kit_pp2', label: 'Наборы PP2', params: { kind: 'kit_pp2' } },
  { key: 'no_files', label: 'Нет файлов', params: { kind: 'all', files: 'missing' } },
  { key: 'no_wb', label: 'Нет на WB', params: { kind: 'all', marketplace: 'no_wb' } },
  { key: 'no_ozon', label: 'Нет на OZON', params: { kind: 'all', marketplace: 'no_ozon' } },
  { key: 'no_ym', label: 'Нет на ЯМ', params: { kind: 'all', marketplace: 'no_ym' } },
  { key: 'has_card', label: 'Есть карточка', params: { kind: 'all', hasCard: 'true' } },
  { key: 'archived', label: 'Архив', params: { kind: 'all', archived: 'true' } },
]

export const DEFAULT_GINGERBREAD_TAB: GingerbreadTabKey = 'kit'

export function tabParams(tab: GingerbreadTabKey): GingerbreadFilterParams {
  return GINGERBREAD_TABS.find((definition) => definition.key === tab)?.params ?? { kind: 'all' }
}

// ── Фильтры → параметры запроса ─────────────────────────────────────

/**
 * Мультивыбор → строка через запятую. Запятая внутри значения экранируется
 * (`\,`), как в каталоге товаров: «Юмор, мемы» иначе развалилось бы на два
 * значения. Разбор — `parseCsv` в product-catalog.service.
 */
export function joinCsv(values: string[]): string | undefined {
  if (!values.length) return undefined
  return values.map((value) => value.replace(/\\/g, '\\\\').replace(/,/g, '\\,')).join(',')
}

/**
 * Состояние фильтров и вкладки → параметры серверного запроса. Вкладка
 * побеждает фильтры страницы там, где они говорят об одном и том же
 * (`files`, `marketplace`).
 */
export function toGingerbreadParams(
  filters: GingerbreadFilters,
  tab: GingerbreadTabKey,
): GingerbreadFilterParams {
  const fromTab = tabParams(tab)
  const params: GingerbreadFilterParams = {
    search: filters.search || undefined,
    theme: joinCsv(filters.theme),
    brand: joinCsv(filters.brand),
    tags: filters.tagIds.length ? filters.tagIds.join(',') : undefined,
    noTags: filters.noTags ? 'true' : undefined,
    shapeMode: filters.shapeMode || undefined,
    files: filters.files || undefined,
    design_state: joinCsv(filters.designState),
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    articles: filters.articles.length ? filters.articles.join(',') : undefined,
    gender: joinCsv(filters.gender),
    age: joinCsv(filters.age),
    occasion: joinCsv(filters.occasion),
    seasonal: filters.seasonal || undefined,
    legal_status: joinCsv(filters.legal),
    persona: joinCsv(filters.persona),
    designType: joinCsv(filters.designType),
    designCategory: joinCsv(filters.designCategory),
    marketplace: filters.marketplace || undefined,
    one_c_status: filters.oneC || undefined,
    hasBarcode: filters.barcode ? (filters.barcode === 'has' ? 'true' : 'false') : undefined,
    hasPreview: filters.preview ? (filters.preview === 'has' ? 'true' : 'false') : undefined,
    ...fromTab,
  }
  // Пустые условия в запрос не попадают (и не мешают сравнению параметров)
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as GingerbreadFilterParams
}

/** Предел фильтра по списку артикулов: список уходит в строку GET-запроса. */
export const ARTICLE_LIST_LIMIT = 500

/** Число активных фильтров панели «Все фильтры» — бейдж на кнопке. */
export function countPanelFilters(filters: GingerbreadFilters): number {
  return [
    filters.gender.length > 0,
    filters.age.length > 0,
    filters.occasion.length > 0,
    Boolean(filters.seasonal),
    filters.legal.length > 0,
    filters.persona.length > 0,
    filters.designType.length > 0,
    filters.designCategory.length > 0,
    Boolean(filters.marketplace),
    Boolean(filters.oneC),
    Boolean(filters.barcode),
    Boolean(filters.preview),
  ].filter(Boolean).length
}

export function hasActiveFilters(filters: GingerbreadFilters): boolean {
  return Boolean(
    filters.search ||
      filters.theme.length ||
      filters.brand.length ||
      filters.shapeMode ||
      filters.files ||
      filters.tagIds.length ||
      filters.noTags ||
      filters.designState.length ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.articles.length ||
      countPanelFilters(filters) > 0,
  )
}

/** Число активных фильтров без поиска. */
export function countActiveFilters(filters: GingerbreadFilters): number {
  return (
    [
      filters.theme.length > 0,
      filters.brand.length > 0,
      Boolean(filters.shapeMode),
      Boolean(filters.files),
      filters.tagIds.length > 0 || filters.noTags,
      filters.designState.length > 0,
      Boolean(filters.dateFrom || filters.dateTo),
      filters.articles.length > 0,
    ].filter(Boolean).length + countPanelFilters(filters)
  )
}

/**
 * Серверный фильтр, который можно передать фоновой пересборке визуалов.
 *
 * Пересборка принимает фильтр каталога товаров и про `kind`, `files`,
 * `shapeMode`, `hasCard` ничего не знает: передав его как есть, мы бы пересобрали
 * не пряники, а весь каталог. Поэтому вид сводится к категориям PP0/PP1, а
 * фильтры, которые категориями не выразить, фоновую пересборку запрещают.
 */
export function backgroundRegenFilter(
  params: GingerbreadFilterParams,
  categories: Array<{ id: number; prefix?: string }>,
): Record<string, unknown> | null {
  if (params.files || params.shapeMode || params.hasCard) return null
  const wanted =
    params.kind === 'single'
      ? ['PP0']
      : params.kind === 'kit'
        ? ['PP1']
        : params.kind === 'kit_pp2'
          ? ['PP2']
          : ['PP0', 'PP1']
  const ids = categories
    .filter((category) => wanted.includes(String(category.prefix || '').toUpperCase()))
    .map((category) => category.id)
  if (!ids.length) return null
  const { kind: _kind, files: _files, shapeMode: _shapeMode, ...rest } = params
  return { ...rest, category: ids.join(',') }
}

// ── Форматирование ──────────────────────────────────────────────────

export const KIND_LABEL = { single: 'Пряник', kit: 'Набор' } as const

/** «64 × 80 мм»; у набора размера контура нет. */
export function formatSize(block?: GingerbreadBlock | null): string {
  if (!block || block.widthMm == null || block.heightMm == null) return '—'
  return formatSizeMm(block.widthMm, block.heightMm)
}

/** «12,4 см²». */
export function formatArea(areaCm2: number | null | undefined): string {
  return areaCm2 == null || !Number.isFinite(areaCm2)
    ? '—'
    : `${formatNumber(Math.round(areaCm2 * 10) / 10)} см²`
}

export function formatGrams(grams: number | null | undefined): string {
  return formatGramsOne(grams)
}

/** Набор PP2: круги макета «Капк 5» (у PP1 и PP0 `origin` нет). */
export function isPp2Kit(block?: GingerbreadBlock | null): boolean {
  return block?.kind === 'kit' && block.origin === 'kapk5'
}

/** Вес посчитан по составу, а не взят из товара (PP2). */
export function isComputedWeight(block?: GingerbreadBlock | null): boolean {
  return block?.weightSource === 'computed'
}

export const COMPUTED_WEIGHT_HINT = 'вес товара берётся из категории, здесь — расчёт по кругам'

/** «51 г / 66,5 г» — нетто и брутто в одну строку. */
export function formatWeightPair(block?: GingerbreadBlock | null): string {
  if (!block || (block.weightNetG == null && block.weightGrossG == null)) return '—'
  return `${formatGramsOne(block.weightNetG)} / ${formatGramsOne(block.weightGrossG)}`
}

/** Форма пряника: «Свободная», название справочной формы или прочерк. */
export function formatShape(block?: GingerbreadBlock | null): string {
  if (isPp2Kit(block)) return block?.shapeName || '—'
  if (!block || block.kind === 'kit') return '—'
  if (block.shapeMode === 'unified') return block.shapeName || 'Справочник'
  if (block.shapeMode === 'free') return 'Свободная'
  return '—'
}

/** Состав: «6 шт» у набора, «в наборах: 2» у пряника. */
export function formatComposition(block?: GingerbreadBlock | null): string {
  if (!block) return '—'
  if (block.kind === 'kit') {
    const count = block.itemsCount ?? block.components.length
    return count > 0 ? `${formatNumber(count)} шт` : '—'
  }
  return `в наборах: ${formatNumber(block.usedIn.length)}`
}

/**
 * Доля листа А4 на позицию набора: 1/N, как в таблице «Раскладка». У штучного
 * пряника доля зависит от числа копий на листе — каталог её не знает.
 */
export function formatSheetShare(block?: GingerbreadBlock | null): string {
  if (!block || block.kind !== 'kit') return '—'
  const count = block.itemsCount ?? block.components.length
  if (!count || count < 1) return '—'
  const share = (1 / count).toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `1/${count} (${share})`
}

/** Названия кругов набора PP2 одной строкой — подсказка к ячейке «Состав». */
export function circlesTooltip(block?: GingerbreadBlock | null): string | undefined {
  if (!isPp2Kit(block) || !block?.components.length) return undefined
  return block.components
    .map((component, index) => `${component.position ?? index + 1}. ${component.name || component.title || 'Круг'}`)
    .join('\n')
}

/** Все три файла на месте; у набора PP2 печатью служит один лист JPG. */
export function filesComplete(block?: GingerbreadBlock | null): boolean {
  if (isPp2Kit(block)) return Boolean(block?.files.sheetJpg)
  return Boolean(block?.files.sheetJpg && block.files.plt && block.files.stl)
}

export interface FileFlag {
  key: 'jpg' | 'plt' | 'stl'
  label: 'JPG' | 'PLT' | 'STL'
  present: boolean
  hint: string
}

/** Значки файлов JPG · PLT · STL с подсказкой. */
export function fileFlags(block?: GingerbreadBlock | null): FileFlag[] {
  const files = block?.files
  const flags: FileFlag[] = [
    { key: 'jpg', label: 'JPG', present: Boolean(files?.sheetJpg), hint: 'Лист для печати' },
    { key: 'plt', label: 'PLT', present: Boolean(files?.plt), hint: 'Файл плоттера' },
    { key: 'stl', label: 'STL', present: Boolean(files?.stl), hint: 'Форма под тесто' },
  ]
  // PP2: PLT собирается вне ПИМ, форму строят по требованию — отсутствующие значки не показываем
  return isPp2Kit(block) ? flags.filter((flag) => flag.key === 'jpg' || flag.present) : flags
}

export function jobHref(jobId: string): string {
  return `/gingerbread-generator/jobs/${encodeURIComponent(jobId)}`
}

export function catalogHref(article: string): string {
  return `/gingerbreads?article=${encodeURIComponent(article)}`
}

/** Превью плитки: файл листа из блока `gingerbread`. */
export function previewOf(row: GingerbreadRow): string | null {
  return row.gingerbread?.files.previewUrl || null
}

/** Счётчик фасета (`facets[field][value]`); undefined — фасета нет. */
export function facetCount(
  facets: Record<string, unknown> | null,
  field: string,
  value: string,
): number | undefined {
  const map = facets?.[field]
  if (!map || typeof map !== 'object') return undefined
  const count = (map as Record<string, unknown>)[value]
  return typeof count === 'number' ? count : undefined
}

/** Значения измерения фасета — для списков, ограниченных каталогом пряников. */
export function facetValues(facets: Record<string, unknown> | null, field: string): string[] {
  const map = facets?.[field]
  if (!map || typeof map !== 'object') return []
  return Object.keys(map as Record<string, unknown>).filter(Boolean)
}

/** Значения справочника плюс те, что пришли в фасетах (отсортированы по алфавиту). */
export function withFacetValues(
  values: string[],
  facets: Record<string, unknown> | null,
  field: string,
): string[] {
  return [...new Set([...values, ...facetValues(facets, field)])].sort((a, b) => a.localeCompare(b, 'ru'))
}

// ── Профили столбцов ────────────────────────────────────────────────

/** Профиль столбцов вкладки: «Пряники» и «Наборы» (обе вкладки наборов) — свои, остальные смотрят на оба вида. */
export function profileForTab(tab: GingerbreadTabKey): GingerbreadColumnProfile {
  if (tab === 'single') return 'single'
  if (tab === 'kit' || tab === 'kit_pp2') return 'kit'
  return 'mixed'
}

// ── Группировка («Вид») ─────────────────────────────────────────────

/** № тематики макета-донора, если бэкенд его отдал в строке. */
export function themeNumberOf(row: GingerbreadRow): number | null {
  const raw = (row.design as { theme_number?: unknown } | undefined)?.theme_number
  if (raw == null || raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Строки текущей страницы → группы выбранного вида. Порядок групп — по первому появлению,
 * у брендов — по размеру группы (как в «Каталоге макетов»).
 */
export function groupRows(rows: GingerbreadRow[], grouping: GingerbreadGrouping): GingerbreadGroup[] {
  if (grouping === 'none') return [{ key: 'all', title: '', rows }]

  const groups = new Map<string, GingerbreadGroup>()
  const push = (key: string, row: GingerbreadRow, init: () => Omit<GingerbreadGroup, 'key' | 'rows'>) => {
    const existing = groups.get(key)
    if (existing) existing.rows.push(row)
    else groups.set(key, { key, ...init(), rows: [row] })
  }

  for (const row of rows) {
    if (grouping === 'theme') {
      const number = themeNumberOf(row)
      const text = row.design?.theme?.trim() || ''
      const key = number != null ? `t:${number}` : text ? `t:${text}` : 't:none'
      push(key, row, () => ({
        title: text || (number != null ? `Тематика № ${number}` : 'Без тематики'),
        themeNumber: number ?? undefined,
      }))
    } else if (grouping === 'brand') {
      const brand = row.design?.brand_ru?.trim() || ''
      push(`b:${brand || 'none'}`, row, () => ({
        title: brand || 'Без бренда',
        legalStatus: row.design?.legal_status || null,
      }))
    } else if (grouping === 'job') {
      const jobId = row.gingerbread?.jobId || null
      push(`j:${jobId ?? 'none'}`, row, () => ({
        title: jobId ? row.gingerbread?.jobName || jobId : 'Без задания',
        jobId,
      }))
    } else {
      const shape = formatShape(row.gingerbread)
      push(`s:${shape}`, row, () => ({ title: shape === '—' ? 'Без формы' : shape }))
    }
  }

  const result = [...groups.values()]
  return grouping === 'brand' ? result.sort((a, b) => b.rows.length - a.rows.length) : result
}
